/** @jsxImportSource @emotion/react */
import { formatErrorResponse } from '@quickbit/backoffice-core-frontend';
import {
  useTheme,
  Money,
  addScaleToMoney,
  removeScaleFromMoney,
  Snackbar,
  Typography,
  Modal,
  Button,
} from '@quickbit/qb-design-system';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsyncFn, useUpdateEffect } from 'react-use';
import { useGetRecipients } from 'api/walletRecipientApi';
import { Layout } from 'components';
import { Markdown } from 'components/Markdown';
import {
  createSendPreview,
  completeSendOrder,
  useTravelRuleStatusPolling,
} from './api';
import { useSendData } from './context/Send';
import { CurrencyAmountInput } from './CurrencyAmountInput';
import { Destination } from './Destination';

const USDT_ETH_REG_EX = new RegExp('^0x[a-fA-F0-9]{40}$');

export const isValidUsdcEthAddress = (address: string): boolean =>
  USDT_ETH_REG_EX.test(address);

export const Details = () => {
  const navigate = useNavigate();
  const { walletBalance, walletBalanceReference, merchantId } = useSendData();

  const { spacing, palette, sizing } = useTheme();
  const [isMax, setIsMax] = useState(false);
  const [label, setLabel] = useState('');
  const [address, setAddress] = useState('');

  const [travelRuleWindow, setTravelRuleWindow] = useState<Window | null>(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [amount, setAmount] = useState<Money>({
    amount: 0,
    currencyCode: 'USDC',
  });
  const {
    data: recipients,
    isLoading: recipientsLoading,
    error: recipientsError,
  } = useGetRecipients({
    merchantId,
    currency: amount.currencyCode,
  });

  const web_url = process.env.REACT_APP_QB_WEB_URL || 'INVALID_URL';

  const [showSendError, setShowSendError] = useState(false);

  if (!walletBalance || !walletBalanceReference || !merchantId) {
    throw Error(
      `Details - Invalid state ${JSON.stringify({
        walletBalance,
        walletBalanceReference,
        merchantId,
      })}`
    );
  }

  const [
    { value: sendValue, error: sendError, loading: sendLoading },
    completeSend,
  ] = useAsyncFn(async (previewId: string) => {
    await completeSendOrder(previewId);
    setTimeout(() => {
      // navigate doesn't cause a full page reload
      // so the wallet doesn't get updated because of state
      window.location.href = '/wallet';
    }, 1000);
    return true;
  }, []);

  const openTravelRuleForm = (previewId: string) => {
    const openedWindow = window.open(
      `${web_url}/travel-rule-beneficiary-info/${previewId}`
    );
    if (!openedWindow) {
      setPopupBlocked(true);
    } else {
      setTravelRuleWindow(openedWindow);
    }
  };

  const [
    {
      value: initiateSendValue,
      error: sendPreviewError,
      loading: sendPreviewLoading,
    },
    createSendPreviewFn,
  ] = useAsyncFn(async () => {
    const previewResponse = await createSendPreview(amount.currencyCode, {
      amount: addScaleToMoney(amount),
      address,
      isMax,
    });
    if (
      !previewResponse.isInternal &&
      !previewResponse.hasTravelRuleCollectedData
    ) {
      setIsPollingEnabled(true);
      openTravelRuleForm(previewResponse.previewId);
    } else {
      await completeSend(previewResponse.previewId);
    }
    return previewResponse.previewId;
  }, [merchantId, amount, address, isMax, completeSend]);

  const {
    data: travelRuleStatus,
    error: travelRuleInfoError,
    isFetching: isPollingTravelRule,
  } = useTravelRuleStatusPolling({
    sendPreviewId: initiateSendValue,
    enabled: isPollingEnabled,
  });

  const readyToSend =
    travelRuleStatus?.status === 'READY' ||
    travelRuleStatus?.status === 'NEEDS_POO';

  const handleCompleteSend = () => {
    if (initiateSendValue && readyToSend) void completeSend(initiateSendValue);
  };

  // Close the travel rule window when status becomes READY
  useEffect(() => {
    if (travelRuleStatus?.status === 'READY' && travelRuleWindow) {
      travelRuleWindow.close();
      setTravelRuleWindow(null);
    }
  }, [travelRuleStatus?.status, travelRuleWindow]);

  const error =
    sendPreviewError || travelRuleInfoError || sendError || recipientsError;

  useUpdateEffect(() => {
    setShowSendError(!!error);
  }, [error]);

  const loading =
    sendPreviewLoading ||
    sendLoading ||
    isPollingTravelRule ||
    recipientsLoading;

  const approvedRecipientsForCurrency =
    recipients?.filter(
      (recipient) =>
        recipient.approvedBy != null &&
        recipient.currencyCode === amount.currencyCode
    ) || [];

  const setRecipient = useCallback(
    ({ address, label }: { address: string; label: string }) => {
      setAddress(address);
      setLabel(label);
    },
    [setAddress, setLabel]
  );

  return (
    <>
      <Layout
        title="Send USDC"
        primaryButtonProps={{
          text: readyToSend ? 'Confirm and send' : 'Send',
          onClick: readyToSend ? handleCompleteSend : createSendPreviewFn,
          disabled:
            amount.amount < 10 || !isValidUsdcEthAddress(address) || loading,
          loading: loading,
        }}
        onBack={() => navigate('/wallet')}
      >
        <div css={{ marginTop: spacing.s }}>
          <CurrencyAmountInput
            value={amount}
            onChange={(e) => {
              if (isMax) {
                setIsMax(false);
              }
              setAmount(e);
            }}
            onMax={() => {
              setAmount({
                ...amount,
                amount:
                  amount.currencyCode === 'USDC'
                    ? removeScaleFromMoney(walletBalance)
                    : removeScaleFromMoney(walletBalanceReference),
              });
              setIsMax(true);
            }}
            disabled={!removeScaleFromMoney(walletBalance)}
            availableAmount={walletBalance}
          />
        </div>
        <Destination
          recipients={approvedRecipientsForCurrency}
          address={address}
          label={label}
          onChangeRecipient={setRecipient}
        />
        {address && !isValidUsdcEthAddress(address) && (
          <div style={{ marginTop: spacing.xxs }}>
            <Snackbar
              message="We only support USDC on the Ethereum network (ERC20 based). This address has the wrong format."
              variant="primary"
              showAsBanner={true}
            />
          </div>
        )}
        {isPollingTravelRule && initiateSendValue && (
          <Markdown
            markdown={`To comply with regulations, you need to provide information about the recipient. [Click here](${web_url}/travel-rule-beneficiary-info/${initiateSendValue}) to go to the form.`}
            styles={{
              color: palette.grey.default,
              fontSize: '0.85rem',
            }}
          />
        )}
        {initiateSendValue && readyToSend && (
          <div css={{ marginTop: spacing.xxs }}>
            <Typography color={palette.grey.default} variant="bodySmall">
              You have provided the necessary information. You can now send the
              funds.
            </Typography>
          </div>
        )}
      </Layout>
      {showSendError && error && (
        <Snackbar
          message={formatErrorResponse(error)}
          variant="danger"
          open={showSendError}
          onClose={() => {
            setShowSendError(false);
          }}
        />
      )}
      <Snackbar
        message={`Sent ${amount.amount} ${amount.currencyCode} to ${address} successfully`}
        variant="success"
        open={!!sendValue}
      />
      {popupBlocked && initiateSendValue && (
        <Modal
          closeModal={() => {
            setPopupBlocked(false);
          }}
        >
          <div
            css={{
              display: 'grid',
              gap: spacing.xs,
              maxWidth: sizing.small,
              textAlign: 'center',
            }}
          >
            <Typography variant="bodySmall" color={palette.danger.default}>
              Travel rule form window blocked from opening, open the form and
              fill in all the details to continue
            </Typography>
            <Button
              onClick={() => {
                setPopupBlocked(false);
                openTravelRuleForm(initiateSendValue);
              }}
              text="Open travel rule form"
            />
          </div>
        </Modal>
      )}
    </>
  );
};
